// SDK Client wrapper for MutagenT API
/// <reference lib="dom" />
import { Mutagent, HTTPClient } from '@mutagent/sdk';
import type { Provider as SDKProviderEnum } from '@mutagent/sdk/models';
import type { UpdatePromptRequest } from '@mutagent/sdk/models/operations';
import type { Prompt, Dataset, DatasetItem, Evaluation, OptimizeJob as OptimizationJob, OptimizeStatus, Agent, CreateAgentInput, UpdateAgentInput, PromptExecutionResult, Workspace, Provider, ProviderTestResult } from '../types/index.js';
import { ApiError, AuthenticationError, MutagentError, WorkspaceContextError } from './errors.js';
import { getApiKey, loadConfig } from './config.js';
import { extractScorecardDetails } from './scorecard-extraction.js';

/**
 * SDK Client Wrapper
 *
 * This class wraps the real @mutagent/sdk to provide a consistent interface
 * for CLI commands. It maps the SDK's method signatures to the interface
 * expected by the CLI.
 */
class SDKClientWrapper {
  private sdk: Mutagent;
  private apiKey: string;
  private endpoint: string;
  private workspaceId?: string;
  private organizationId?: string;

  constructor(opts: { apiKey: string; serverURL?: string; workspaceId?: string; organizationId?: string }) {
    this.apiKey = opts.apiKey;
    this.endpoint = opts.serverURL ?? 'http://localhost:3003';
    this.workspaceId = opts.workspaceId;
    this.organizationId = opts.organizationId;

    // Create HTTP client with tenancy header injection
    const httpClient = new HTTPClient();
    httpClient.addHook("beforeRequest", (req) => {
      if (this.workspaceId) req.headers.set("x-workspace-id", this.workspaceId);
      if (this.organizationId) req.headers.set("x-organization-id", this.organizationId);
      return req;
    });

    this.sdk = new Mutagent({
      security: {
        apiKey: this.apiKey,
      },
      serverURL: this.endpoint,
      httpClient,
    });
  }

  /**
   * Helper to handle SDK errors and convert them to CLI errors.
   * Masks Speakeasy SDK internal errors (SDKValidationError, SDKError, etc.)
   * so users see clean, actionable messages instead of raw stack traces.
   */
  private handleError(error: unknown): never {
    if (error && typeof error === 'object') {
      const err = error as { statusCode?: number; message?: string; body?: string; name?: string; rawValue?: unknown; rawMessage?: string };

      if (err.statusCode === 401) {
        throw new AuthenticationError();
      }
      if (err.statusCode === 403) {
        throw new ApiError(403, err.message ?? err.body ?? 'Access denied');
      }

      // Detect workspace/org context errors from the server
      const errMessage = (err.message ?? err.body ?? '').toLowerCase();
      if (
        errMessage.includes('workspace') && (errMessage.includes('missing') || errMessage.includes('required')) ||
        errMessage.includes('x-workspace-id')
      ) {
        throw new WorkspaceContextError(err.message ?? err.body ?? undefined);
      }

      if (err.statusCode) {
        throw new ApiError(err.statusCode, err.message ?? err.body ?? 'Unknown error');
      }

      // Catch Speakeasy SDK internal errors (SDKValidationError, SDKError, etc.)
      if (
        err.name === 'SDKValidationError' ||
        err.name === 'SDKError' ||
        err.rawValue !== undefined ||
        err.rawMessage !== undefined
      ) {
        const friendlyMessage = err.rawMessage ?? err.message ?? 'Request failed';
        throw new MutagentError(
          'REQUEST_FAILED',
          friendlyMessage,
          'Check your input and try again. Run the command with --help for usage details.'
        );
      }
    }

    // Catch network errors (fetch failures, DNS resolution, etc.)
    if (error instanceof TypeError && typeof error.message === 'string') {
      if (error.message.includes('fetch') || error.message.includes('network') || error.message.includes('ECONNREFUSED')) {
        throw new MutagentError(
          'NETWORK_ERROR',
          'Unable to connect to the MutagenT server',
          'Check your internet connection and verify the server URL with: mutagent config list'
        );
      }
    }

    // Last resort: wrap unknown errors in MutagentError
    if (error instanceof Error) {
      throw new MutagentError(
        'REQUEST_FAILED',
        error.message,
        'Run the command with --help for usage details'
      );
    }

    throw new MutagentError(
      'UNKNOWN_ERROR',
      'An unexpected error occurred',
      'Run the command with --help for usage details'
    );
  }

  /**
   * Helper for direct HTTP requests (for endpoints not yet in SDK)
   */
  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    // Convert Headers object to plain object if needed
    const optHeaders = options?.headers;
    let extraHeaders: Record<string, string> = {};
    if (optHeaders instanceof Headers) {
      optHeaders.forEach((value, key) => {
        extraHeaders[key] = value;
      });
    } else if (optHeaders) {
      extraHeaders = optHeaders as Record<string, string>;
    }

    // Build tenancy headers from stored config
    const tenancyHeaders: Record<string, string> = {};
    if (this.workspaceId) tenancyHeaders['x-workspace-id'] = this.workspaceId;
    if (this.organizationId) tenancyHeaders['x-organization-id'] = this.organizationId;

    const response = await fetch(`${this.endpoint}${path}`, {
      ...options,
      headers: {
        'x-api-key': this.apiKey,
        'Content-Type': 'application/json',
        ...tenancyHeaders,
        ...extraHeaders,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new AuthenticationError();
      }
      if (response.status === 403) {
        throw new ApiError(403, await response.text() || 'Access denied');
      }
      const error = await response.text();
      // Detect workspace context errors from the server response body
      const errorLower = error.toLowerCase();
      if (
        errorLower.includes('workspace') && (errorLower.includes('missing') || errorLower.includes('required')) ||
        errorLower.includes('x-workspace-id')
      ) {
        throw new WorkspaceContextError(error);
      }
      throw new ApiError(response.status, error);
    }

    return response.json() as Promise<T>;
  }

  // =========================================================================
  // PROMPTS
  // =========================================================================

  async listPrompts(): Promise<Prompt[]> {
    try {
      const response = await this.sdk.prompt.listPrompts();
      return response.result.data as Prompt[];
    } catch (error) {
      this.handleError(error);
    }
  }

  async getPrompt(id: string): Promise<Prompt> {
    try {
      const response = await this.sdk.prompt.getPrompt({ id: parseInt(id, 10) });
      return response as Prompt;
    } catch (error) {
      this.handleError(error);
    }
  }

  async createPrompt(data: Partial<Prompt>): Promise<Prompt> {
    try {
      const response = await this.sdk.prompt.createPrompt({
        name: data.name ?? '',
        description: data.description ?? undefined,
        version: data.version ?? undefined,
        isLatest: data.isLatest ?? undefined,
        systemPrompt: data.systemPrompt ?? undefined,
        humanPrompt: data.humanPrompt ?? undefined,
        rawPrompt: data.rawPrompt ?? undefined,
        inputSchema: data.inputSchema ?? undefined,
        outputSchema: data.outputSchema ?? undefined,
        metadata: data.metadata ?? undefined,
        tags: data.tags ?? undefined,
      });
      return response as Prompt;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updatePrompt(id: string, data: Partial<Prompt>): Promise<Prompt> {
    try {
      const body: Record<string, unknown> = {
        description: data.description ?? undefined,
        isLatest: data.isLatest ?? undefined,
        systemPrompt: data.systemPrompt ?? undefined,
        humanPrompt: data.humanPrompt ?? undefined,
        rawPrompt: data.rawPrompt ?? undefined,
        inputSchema: data.inputSchema as Record<string, never> | undefined,
        outputSchema: data.outputSchema as Record<string, never> | undefined,
        metadata: data.metadata as Record<string, never> | undefined,
        tags: data.tags ?? undefined,
      };
      if (data.name !== undefined) {
        body.name = data.name;
      }
      const updateArgs: UpdatePromptRequest = { id: parseInt(id, 10), body };
      const response = await this.sdk.prompt.updatePrompt(updateArgs);
      return response as Prompt;
    } catch (error) {
      this.handleError(error);
    }
  }

  async deletePrompt(id: string, options?: { force?: boolean }): Promise<void> {
    try {
      if (options?.force) {
        // Use direct request to pass force query param (SDK method doesn't support it)
        // CRITICAL: Route is /api/prompt/:id (singular), NOT /api/prompts/:id (plural)
        await this.request(`/api/prompt/${id}?force=true`, { method: 'DELETE' });
      } else {
        await this.sdk.prompt.deletePrompt({ id: parseInt(id, 10) });
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  // =========================================================================
  // DATASETS (scoped to prompt)
  // Using direct fetch as SDK types don't match CLI expectations
  // =========================================================================

  async getDataset(datasetId: string | number): Promise<Dataset> {
    try {
      return await this.request<Dataset>(`/api/prompts/datasets/${String(datasetId)}`);
    } catch (error) {
      this.handleError(error);
    }
  }

  async getDatasetItem(itemId: string | number): Promise<DatasetItem> {
    try {
      return await this.request<DatasetItem>(`/api/prompts/dataset-items/${String(itemId)}`);
    } catch (error) {
      this.handleError(error);
    }
  }

  async listDatasets(promptId: string): Promise<Dataset[]> {
    try {
      const response = await this.request<Dataset[] | { data: Dataset[] }>(`/api/prompt/${promptId}/datasets`);
      return Array.isArray(response) ? response : response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async addDataset(promptId: string, fileContent: string, name?: string): Promise<Dataset & { itemCount?: number; missingExpectedOutputCount?: number }> {
    try {
      // Step 1: Parse content to extract items
      let parsed: unknown;
      try {
        parsed = JSON.parse(fileContent);
      } catch {
        // If not parseable as JSON (e.g., CSV), no items to extract
        parsed = null;
      }

      // Determine dataset name and items
      let datasetName = name ?? `Dataset ${new Date().toISOString().slice(0, 16)}`;
      let items: Record<string, unknown>[] | undefined;

      if (
        parsed !== null &&
        !Array.isArray(parsed) &&
        typeof parsed === 'object' &&
        'name' in (parsed as Record<string, unknown>)
      ) {
        // Object with 'name' field: treat as dataset metadata (may contain items)
        const meta = parsed as Record<string, unknown>;
        datasetName = (meta.name as string | undefined) ?? datasetName;
        if (Array.isArray(meta.items)) {
          items = meta.items as Record<string, unknown>[];
        }
      } else if (Array.isArray(parsed)) {
        items = parsed as Record<string, unknown>[];
      }

      // Step 2: Create dataset metadata via POST /api/prompt/:id/datasets
      const dataset = await this.request<Dataset>(`/api/prompt/${promptId}/datasets`, {
        method: 'POST',
        body: JSON.stringify({
          name: datasetName,
        }),
      });

      // Step 3: Bulk upload items if any via POST /api/prompts/datasets/:id/items/bulk
      let itemCount = 0;
      if (items && items.length > 0) {
        // Map items to the backend schema: { input, expectedOutput, ... }
        const mappedItems = items.map((item, index) => ({
          input: item.input ?? item,
          expectedOutput: item.expectedOutput ?? item.expected_output ?? undefined,
          name: (item.name as string | undefined) ?? `Item ${String(index + 1)}`,
          userFeedback: item.userFeedback as string | undefined,
          systemFeedback: item.systemFeedback as string | undefined,
          labels: item.labels as string[] | undefined,
          metadata: item.metadata as Record<string, unknown> | undefined,
        }));

        const missingExpectedOutputCount = mappedItems.filter(
          item => item.expectedOutput === undefined
        ).length;

        await this.request(`/api/prompts/datasets/${String(dataset.id)}/items/bulk`, {
          method: 'POST',
          body: JSON.stringify({ items: mappedItems }),
        });
        itemCount = mappedItems.length;

        return { ...dataset, itemCount, missingExpectedOutputCount };
      }

      return { ...dataset, itemCount };
    } catch (error) {
      this.handleError(error);
    }
  }

  async listDatasetItems(datasetId: string): Promise<DatasetItem[]> {
    try {
      const response = await this.request<DatasetItem[] | { data: DatasetItem[] }>(`/api/prompts/datasets/${datasetId}/items`);
      return Array.isArray(response) ? response : response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async deleteDataset(_promptId: string, datasetId: string, options?: { force?: boolean }): Promise<void> {
    try {
      const query = options?.force ? '?force=true' : '';
      await this.request(`/api/prompts/datasets/${datasetId}${query}`, { method: 'DELETE' });
    } catch (error) {
      this.handleError(error);
    }
  }

  // =========================================================================
  // EVALUATIONS - Using direct fetch for consistent interface
  // =========================================================================

  async listEvaluations(promptId: string): Promise<Evaluation[]> {
    try {
      const response = await this.request<{ data: Evaluation[] }>(`/api/prompts/evaluations?promptId=${promptId}`);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getEvaluation(evaluationId: string): Promise<Evaluation> {
    try {
      return await this.request<Evaluation>(`/api/prompts/evaluations/${evaluationId}`);
    } catch (error) {
      this.handleError(error);
    }
  }

  async createEvaluation(promptId: string, data: {
    name: string;
    description?: string;
    evalConfig?: Record<string, unknown>;
    llmConfig?: Record<string, unknown>;
    tags?: string[];
    metadata?: Record<string, unknown>;
  }): Promise<Evaluation> {
    try {
      return await this.request<Evaluation>('/api/prompts/evaluations', {
        method: 'POST',
        body: JSON.stringify({
          promptId: parseInt(promptId, 10),
          name: data.name,
          description: data.description,
          evalConfig: data.evalConfig,
          llmConfig: data.llmConfig,
          tags: data.tags,
          metadata: data.metadata,
        }),
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async deleteEvaluation(evaluationId: string): Promise<void> {
    try {
      await this.request(`/api/prompts/evaluations/${evaluationId}`, { method: 'DELETE' });
    } catch (error) {
      this.handleError(error);
    }
  }

  // =========================================================================
  // OPTIMIZATIONS - Using direct fetch for consistent interface
  // NOTE: Optimization SDK types are empty stubs (OpenAPI spec gap being fixed).
  // These methods use raw HTTP with CLI-defined types until SDK types are populated.
  // =========================================================================

  async listOptimizations(): Promise<unknown[]> {
    try {
      const response = await this.request<{ data?: unknown[]; total?: number } | unknown[]>('/api/optimizations');
      if (Array.isArray(response)) return response;
      const r = response as { data?: unknown[]; total?: number };
      if (Array.isArray(r.data)) return r.data;
      return [];
    } catch (error) {
      this.handleError(error);
    }
  }

  async listExperiments(filters?: { promptId?: number; status?: string; limit?: number; offset?: number }): Promise<{ experiments: unknown[]; total: number }> {
    try {
      const params = new URLSearchParams();
      if (filters?.promptId !== undefined) params.set('promptId', String(filters.promptId));
      if (filters?.status) params.set('status', filters.status);
      if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
      if (filters?.offset !== undefined) params.set('offset', String(filters.offset));
      const qs = params.toString();
      const response = await this.request<{ data?: unknown[]; experiments?: unknown[]; total?: number } | unknown[]>(`/api/prompts/experiments${qs ? `?${qs}` : ''}`);
      if (Array.isArray(response)) return { experiments: response, total: response.length };
      const r = response as { data?: unknown[]; experiments?: unknown[]; total?: number };
      const experiments = r.experiments ?? r.data ?? [];
      return { experiments, total: r.total ?? experiments.length };
    } catch (error) {
      this.handleError(error);
    }
  }

  async createExperiment(data: {
    promptId: number;
    promptGroupId: string;
    evaluationId: number;
    datasetId: number;
    models: { id: string; model: string; temperature: number }[];
    name?: string;
    datasetItemCount?: number;
  }): Promise<{ experimentId: string; name: string; status: string }> {
    try {
      return await this.request<{ experimentId: string; name: string; status: string }>('/api/prompts/experiments', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async getExperiment(id: string): Promise<Record<string, unknown>> {
    try {
      return await this.request<Record<string, unknown>>(`/api/prompts/experiments/${id}`);
    } catch (error) {
      this.handleError(error);
    }
  }

  async deleteExperiment(id: string): Promise<void> {
    try {
      await this.request(`/api/prompts/experiments/${id}`, { method: 'DELETE' });
    } catch (error) {
      this.handleError(error);
    }
  }

  async executeExperiment(id: string): Promise<{ success: boolean; experimentId: string; status: string }> {
    try {
      return await this.request<{ success: boolean; experimentId: string; status: string }>(
        `/api/prompts/experiments/${id}/execute`,
        { method: 'POST', body: JSON.stringify({}) }
      );
    } catch (error) {
      this.handleError(error);
    }
  }

  async playgroundEval(promptId: string, data: {
    input: Record<string, unknown>;
    evaluationId: number;
    llmConfig?: Record<string, unknown>;
  }): Promise<{
    executionId: string;
    response: string | Record<string, unknown>;
    metadata: { latencyMs: number; tokens?: { prompt?: number; completion?: number } };
    evaluation: { score: number; passed: boolean; reasoning?: string; criteria?: { name: string; score: number; passed: boolean }[] };
  }> {
    try {
      return await this.request<{
        executionId: string;
        response: string | Record<string, unknown>;
        metadata: { latencyMs: number; tokens?: { prompt?: number; completion?: number } };
        evaluation: { score: number; passed: boolean; reasoning?: string; criteria?: { name: string; score: number; passed: boolean }[] };
      }>(`/api/prompt/${promptId}/playground/eval`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async cancelOptimization(jobId: string): Promise<{ success: boolean }> {
    try {
      return await this.request<{ success: boolean }>(`/api/optimization/${jobId}/cancel`, {
        method: 'POST',
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async startOptimization(promptId: string, datasetId: string, evaluationId: string, config?: {
    maxIterations?: number;
    targetScore?: number;
    patience?: number;
    /** backward-compat execution slot (--model) */
    model?: string;
    /** --exec-model: takes precedence over model when both set */
    execModel?: string;
    evalModel?: string;
    optModel?: string;
    /** β #525 — provider-id-first routing */
    providerId?: string;
    evalProviderId?: string;
    optProviderId?: string;
  }): Promise<OptimizationJob> {
    try {
      return await this.request<OptimizationJob>(`/api/prompt/${promptId}/optimize`, {
        method: 'POST',
        body: JSON.stringify({
          datasetId: parseInt(datasetId, 10),
          evaluationId: parseInt(evaluationId, 10),
          config: {
            maxIterations: config?.maxIterations ?? 1,
            targetScore: config?.targetScore ?? 0.8,
            patience: config?.patience,
            // --exec-model sends executionModel; --model sends legacy model field
            ...(config?.execModel ? { executionModel: config.execModel } : {}),
            ...(config?.model ? { model: config.model } : {}),
            ...(config?.evalModel ? { evaluationModel: config.evalModel } : {}),
            ...(config?.optModel ? { optimizationModel: config.optModel } : {}),
            ...(config?.providerId ? { executionProviderId: config.providerId } : {}),
            ...(config?.evalProviderId ? { evaluationProviderId: config.evalProviderId } : {}),
            ...(config?.optProviderId ? { optimizationProviderId: config.optProviderId } : {}),
          },
          executionMode: 'worker_loop',
        }),
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async getOptimizationStatus(jobId: string): Promise<OptimizeStatus> {
    try {
      const raw = await this.request<Record<string, unknown>>(`/api/optimization/${jobId}`);
      // Map API response fields to CLI OptimizeStatus type
      // API returns `id` + `progress`; CLI expects `jobId` + `progressPercent`
      const id = raw.id as string | undefined;
      return {
        ...raw,
        jobId: id ?? jobId,
        progressPercent: typeof raw.progress === 'number' ? raw.progress : 0,
      } as OptimizeStatus;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * T-07: Fetch the server-rendered scorecard digest for a completed job.
   * Returns null if the endpoint is unavailable (e.g., job still running or pre-T-04 server).
   */
  async getOptimizationScorecard(jobId: string): Promise<{
    rendered: string;
    data: unknown[];
  } | null> {
    try {
      const res = await this.request<{ scorecard?: { rendered?: string; data?: unknown[] } }>(
        `/api/optimization/${jobId}/results`
      );
      if (res.scorecard?.rendered) {
        return { rendered: res.scorecard.rendered, data: res.scorecard.data ?? [] };
      }
      return null;
    } catch {
      // Best-effort — older server versions or incomplete jobs return 404/500
      return null;
    }
  }

  async getOptimizationResults(jobId: string): Promise<{
    job: { id: string; promptId: number; status: string; config?: { maxIterations?: number; model?: string } };
    prompt: Prompt;
    bestScore?: number;
    originalScore?: number;
    iterationsCompleted?: number;
    scoreProgression?: number[];
    originalPrompt?: Partial<Prompt>;
    mutatedPromptText?: string;
    originalPromptText?: string;
    criteriaScores?: { name: string; before?: number; after?: number }[];
    datasetResults?: { id: string; beforeScore?: number; afterScore?: number }[];
    failureModes?: { category: string; failures: { description?: string; summary?: string }[] }[];
    mutations?: { label: string; status: 'applied' | 'rejected' | 'pending' | 'skipped'; priority?: string; rationale?: string }[];
    evaluationDetails?: {
      itemId: string; score: number; success: boolean;
      metrics?: { name: string; score: number; success: boolean; failureMode?: string; reasoning?: string; criteria?: { name: string; score: number; success: boolean }[] }[];
    }[];
    /** Server-rendered scorecard digest (from T-04 /results endpoint). Present if server supports it. */
    scorecard?: { rendered: string; data: unknown[] };
  }> {
    try {
      // Compose result from multiple API calls:
      // 1. Job details (status, config, promptId)
      const job = await this.request<{
        id?: string;
        promptId?: number;
        status?: string;
        bestScore?: number;
        currentIteration?: number;
        config?: { maxIterations?: number; model?: string };
      }>(`/api/optimization/${jobId}`);
      // 2. Progression data (score history per iteration)
      const progress = await this.request<{ jobId?: string; progression?: { iteration?: number; score?: number | null }[] }>(`/api/optimization/${jobId}/progress`);
      // 3. Prompt data (current/optimized prompt text)
      const prompt = await this.getPrompt(String(job.promptId ?? ''));
      // 4. Get state snapshots for iteration context (mutated prompt lives here)
      const statesRes = await this.request<{
        states: {
          iteration: number;
          state: Record<string, unknown>;
          score: number | null;
        }[];
      }>(`/api/optimization/${jobId}/states`).catch(() => ({ states: [] as { iteration: number; state: Record<string, unknown>; score: number | null }[] }));
      // 5. T-07: Fetch server-rendered scorecard digest (best-effort)
      const scorecardDigest = await this.getOptimizationScorecard(jobId);

      // Extract data from latest state snapshot
      // The backend normalizes state.current.context into state.iterationContext
      // at ITERATION_END, so both paths exist. We use iterationContext (shortcut)
      // and fall back to state.current.context (raw framework path).
      const latestState = statesRes.states[statesRes.states.length - 1];
      const rawState = latestState?.state ?? {};

      // Get iteration context — try normalized path, then raw framework path
      const iterCtx = (rawState.iterationContext
        ?? (rawState.current as Record<string, unknown> | undefined)?.context) as Record<string, unknown> | undefined;

      // Extract prompt texts
      const basePromptObj = iterCtx?.basePrompt as { prompt?: string } | undefined;
      const currentPromptObj = iterCtx?.currentPrompt as { prompt?: string } | undefined;
      const mutatedPromptText = typeof currentPromptObj?.prompt === 'string'
        ? currentPromptObj.prompt : undefined;
      const originalPromptText = typeof basePromptObj?.prompt === 'string'
        ? basePromptObj.prompt : undefined;

      // Extract detail data from state snapshot for scorecard rendering
      const extracted = extractScorecardDetails(rawState, iterCtx);

      return {
        job: {
          id: job.id ?? jobId,
          promptId: job.promptId ?? 0,
          status: job.status ?? 'unknown',
          config: job.config,
        },
        prompt,
        bestScore: job.bestScore,
        originalScore: extracted.originalScore,
        iterationsCompleted: job.currentIteration,
        scoreProgression: Array.isArray(progress.progression)
          ? progress.progression.map(p => (typeof p.score === 'number' ? p.score : 0))
          : undefined,
        mutatedPromptText,
        originalPromptText,
        criteriaScores: extracted.criteriaScores,
        datasetResults: extracted.datasetResults,
        failureModes: extracted.failureModes,
        mutations: extracted.mutations,
        evaluationDetails: extracted.evaluationDetails,
        ...(scorecardDigest ? { scorecard: scorecardDigest } : {}),
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  // =========================================================================
  // TRACES - Using direct fetch since SDK doesn't have traces
  // =========================================================================

  async listTraces(filters?: { promptId?: string; source?: string }): Promise<unknown[]> {
    const filterRecord: Record<string, string> = {};
    if (filters?.promptId) filterRecord.promptId = filters.promptId;
    if (filters?.source) filterRecord.source = filters.source;
    const params = Object.keys(filterRecord).length > 0 ? new URLSearchParams(filterRecord).toString() : '';
    const response = await this.request<{ data?: unknown[]; total: number }>(`/api/traces${params ? `?${params}` : ''}`);
    return response.data ?? [];
  }

  async getTrace(id: string): Promise<unknown> {
    // Always request full IO payload — CLI users of `mutagent traces get` expect
    // complete span data including input/output (GH #506 slim-default coord).
    return this.request(`/api/traces/${id}?include=io`);
  }

  async analyzeTraces(promptId: string): Promise<Record<string, unknown>> {
    return this.request(`/api/prompts/${promptId}/trace-analysis`);
  }

  // =========================================================================
  // AGENTS
  // =========================================================================

  async listAgents(filters?: { limit?: number; offset?: number; name?: string; status?: string }): Promise<{ data: Agent[]; total: number }> {
    try {
      const response = await this.sdk.agents.listAgents({
        limit: filters?.limit,
        offset: filters?.offset,
        name: filters?.name,
        status: filters?.status,
      });
      return {
        data: response.result.data as unknown as Agent[],
        total: response.result.total,
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  async getAgent(id: string): Promise<Agent> {
    try {
      const response = await this.sdk.agents.getAgent({
        id: parseInt(id, 10),
      });
      return response as unknown as Agent;
    } catch (error) {
      this.handleError(error);
    }
  }

  async createAgent(data: CreateAgentInput): Promise<Agent> {
    try {
      const response = await this.sdk.agents.createAgent({
        name: data.name,
        slug: data.slug,
        description: data.description ?? undefined,
        systemPrompt: data.systemPrompt,
        model: data.model ?? undefined,
        maxTurns: data.maxTurns ?? undefined,
        permissionMode: data.permissionMode ?? undefined,
        allowedTools: data.allowedTools ?? undefined,
        disallowedTools: data.disallowedTools ?? undefined,
        status: data.status ?? undefined,
        isPublic: data.isPublic ?? undefined,
        metadata: data.metadata ?? undefined,
        tags: data.tags ?? undefined,
      });
      return response as unknown as Agent;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateAgent(id: string, data: UpdateAgentInput): Promise<Agent> {
    try {
      const response = await this.sdk.agents.updateAgent({
        id: parseInt(id, 10),
        body: {
          name: data.name ?? undefined,
          description: data.description ?? undefined,
          systemPrompt: data.systemPrompt ?? undefined,
          model: data.model ?? undefined,
          maxTurns: data.maxTurns ?? undefined,
          permissionMode: data.permissionMode ?? undefined,
          allowedTools: data.allowedTools ?? undefined,
          disallowedTools: data.disallowedTools ?? undefined,
          status: data.status ?? undefined,
          isPublic: data.isPublic ?? undefined,
          metadata: data.metadata ?? undefined,
          tags: data.tags ?? undefined,
        },
      });
      return response as unknown as Agent;
    } catch (error) {
      this.handleError(error);
    }
  }

  async deleteAgent(id: string): Promise<void> {
    try {
      await this.sdk.agents.deleteAgent({
        id: parseInt(id, 10),
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  // =========================================================================
  // WORKSPACES
  // =========================================================================

  async listWorkspaces(filters?: { limit?: number; offset?: number }): Promise<{ data: Workspace[]; total: number }> {
    try {
      const response = await this.sdk.workspaces.listWorkspaces({
        includeInactive: false,
      });
      // SDK returns { workspaces: [...], total } but CLI expects { data: [...], total }
      const workspaces = response.workspaces as unknown as Workspace[];
      // Apply client-side pagination if needed
      const offset = filters?.offset ?? 0;
      const limit = filters?.limit ?? workspaces.length;
      return {
        data: workspaces.slice(offset, offset + limit),
        total: response.total,
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  async getWorkspace(id: string): Promise<Workspace> {
    try {
      const response = await this.sdk.workspaces.getWorkspace({
        wsId: id,
      });
      return response as unknown as Workspace;
    } catch (error) {
      this.handleError(error);
    }
  }

  // =========================================================================
  // PLAYGROUND - Execute prompts
  // =========================================================================

  async executePrompt(
    promptId: string,
    input: Record<string, unknown>,
    options?: { model?: string }
  ): Promise<PromptExecutionResult> {
    try {
      return await this.request<PromptExecutionResult>(`/api/prompt/${promptId}/playground/call`, {
        method: 'POST',
        body: JSON.stringify({ input, model: options?.model }),
      });
    } catch (error) {
      this.handleError(error);
    }
  }

  async executePromptStream(
    promptId: string,
    input: Record<string, unknown>,
    options?: { model?: string }
  ): Promise<Response> {
    const response = await fetch(`${this.endpoint}/api/prompt/${promptId}/execute/stream`, {
      method: 'POST',
      headers: {
        'x-api-key': this.apiKey,
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify({ input, ...options }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new AuthenticationError();
      }
      if (response.status === 403) {
        throw new ApiError(403, await response.text() || 'Access denied');
      }
      const error = await response.text();
      // Detect workspace context errors from the server response body
      const errorLower = error.toLowerCase();
      if (
        errorLower.includes('workspace') && (errorLower.includes('missing') || errorLower.includes('required')) ||
        errorLower.includes('x-workspace-id')
      ) {
        throw new WorkspaceContextError(error);
      }
      throw new ApiError(response.status, error);
    }

    return response;
  }

  // =========================================================================
  // PROVIDERS
  // =========================================================================

  async listProviders(filters?: { limit?: number; offset?: number; type?: string }): Promise<{ data: Provider[]; total: number }> {
    try {
      const response = await this.sdk.providerConfigs.listProviders({
        provider: filters?.type,
        includeInactive: true,
        workspaceId: this.workspaceId,
        organizationId: this.organizationId,
      });
      // SDK (Speakeasy-generated) returns ProviderConfigsList: { configs: [...], total }
      // Guard against SDK regen wrapping the payload in a `result` envelope.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = response as any;
      const configs = (raw.result?.configs ?? raw.configs ?? []) as Provider[];
      const total: number = raw.result?.total ?? raw.total ?? configs.length;
      // Apply client-side pagination if needed
      const offset = filters?.offset ?? 0;
      const limit = filters?.limit ?? configs.length;
      return {
        data: configs.slice(offset, offset + limit),
        total,
      };
    } catch (error) {
      this.handleError(error);
    }
  }

  async getProvider(id: string): Promise<Provider> {
    try {
      const response = await this.sdk.providerConfigs.getProvider({
        id: id,
      });
      return response as unknown as Provider;
    } catch (error) {
      this.handleError(error);
    }
  }

  async getModelsCatalog(): Promise<{ models: { id: string; displayName: string; provider: { id: string; name: string; kind: string }; roles: string[] }[] }> {
    try {
      const wsId = this.workspaceId;
      if (!wsId) return { models: [] };
      const qs = `?workspaceId=${encodeURIComponent(wsId)}`;
      return await this.request<{ models: { id: string; displayName: string; provider: { id: string; name: string; kind: string }; roles: string[] }[] }>(`/api/providers/catalog${qs}`);
    } catch {
      return { models: [] };
    }
  }

  getCurrentWorkspaceId(): string | undefined {
    return this.workspaceId;
  }

  getCurrentOrgId(): string | undefined {
    return this.organizationId;
  }

  /**
   * Auto-resolve organization and workspace context when not provided via config.
   * Called once at client init so ALL commands benefit from tenancy headers.
   */
  async ensureContext(): Promise<void> {
    if (this.organizationId && this.workspaceId) return;

    if (!this.organizationId) {
      let orgs = await fetchOrganizations(this.apiKey, this.endpoint).catch(() => []);
      if (orgs.length === 0) {
        // Retry once after 1s for transient network failures
        await new Promise(r => setTimeout(r, 1000));
        orgs = await fetchOrganizations(this.apiKey, this.endpoint).catch(() => []);
      }
      if (orgs.length > 0 && orgs[0]) {
        this.organizationId = orgs[0].id;
      }
    }

    if (!this.workspaceId && this.organizationId) {
      try {
        const response = await this.sdk.workspaces.listWorkspaces({ includeInactive: false });
        const workspaces = response.workspaces as unknown as Workspace[];
        if (workspaces.length > 0) {
          const defaultWs = workspaces.find((ws: Workspace) => ws.isDefault) ?? workspaces[0];
          if (defaultWs) this.workspaceId = String(defaultWs.id);
        }
      } catch {
        /* best-effort */
      }
    }
  }

  async testProvider(id: string): Promise<ProviderTestResult> {
    try {
      const response = await this.sdk.providerConfigs.testProvider({
        id: id,
      });
      return response as unknown as ProviderTestResult;
    } catch (error) {
      this.handleError(error);
    }
  }

  async createProvider(data: {
    name: string;
    provider: string;
    apiKey: string;
    scope?: { workspaceId?: string; organizationId?: string; userId?: string };
    baseUrl?: string;
    isDefault?: boolean;
  }): Promise<Provider> {
    try {
      const response = await this.sdk.providerConfigs.createProvider({
        name: data.name,
        provider: data.provider as SDKProviderEnum,
        apiKey: data.apiKey,
        scope: data.scope ?? {},
        baseUrl: data.baseUrl,
        isDefault: data.isDefault,
      });
      return response as unknown as Provider;
    } catch (error) {
      this.handleError(error);
    }
  }

  async updateProvider(id: string, data: {
    name?: string;
    apiKey?: string;
    isActive?: boolean;
    isDefault?: boolean;
    baseUrl?: string | null;
  }): Promise<Provider> {
    try {
      const response = await this.sdk.providerConfigs.updateProvider({
        id,
        body: {
          name: data.name,
          apiKey: data.apiKey,
          isActive: data.isActive,
          isDefault: data.isDefault,
          baseUrl: data.baseUrl,
        },
      });
      return response as unknown as Provider;
    } catch (error) {
      this.handleError(error);
    }
  }

  async deleteProvider(id: string): Promise<void> {
    try {
      await this.sdk.providerConfigs.deleteProvider({ id });
    } catch (error) {
      this.handleError(error);
    }
  }
}

// Singleton SDK client
let sdkClient: SDKClientWrapper | null = null;

export async function getSDKClient(): Promise<SDKClientWrapper> {
  if (!sdkClient) {
    const apiKey = getApiKey();
    if (!apiKey) {
      throw new AuthenticationError();
    }

    const config = loadConfig();
    sdkClient = new SDKClientWrapper({
      apiKey: apiKey,
      serverURL: config.endpoint,
      workspaceId: config.defaultWorkspace,
      organizationId: config.defaultOrganization,
    });

    // Auto-resolve org + workspace context so all commands get tenancy headers
    await sdkClient.ensureContext().catch((err: unknown) => {
      // Log to stderr (not stdout — don't pollute JSON output)
      if (process.env.MUTAGENT_DEBUG) {
        const message = err instanceof Error ? err.message : String(err);
        process.stderr.write(`[mutagent] Context resolution failed: ${message}\n`);
      }
    });
  }

  return sdkClient;
}

export function resetSDKClient(): void {
  sdkClient = null;
}

export async function validateApiKey(apiKey: string, endpoint: string): Promise<boolean> {
  // Use /api/providers — works with workspace-scoped mg_live_ keys without requiring org context
  try {
    const response = await fetch(`${endpoint}/api/providers`, {
      headers: { 'x-api-key': apiKey },
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Fetch organizations for the authenticated API key.
 * Returns array of { id, name } or empty array on failure.
 */
export async function fetchOrganizations(apiKey: string, endpoint: string): Promise<{ id: string; name: string }[]> {
  try {
    const response = await fetch(`${endpoint}/api/organizations`, {
      headers: { 'x-api-key': apiKey },
    });
    if (!response.ok) return [];
    const data = await response.json() as { data?: { id: string; name: string }[] };
    return data.data ?? [];
  } catch {
    return [];
  }
}

/**
 * Fetch workspaces for the given organization.
 * Returns array of { id, name, isDefault } or empty array on failure.
 */
export async function fetchWorkspaces(apiKey: string, endpoint: string, orgId: string): Promise<{ id: string; name: string; isDefault?: boolean }[]> {
  try {
    const response = await fetch(`${endpoint}/api/workspaces`, {
      headers: { 'x-api-key': apiKey, 'x-organization-id': orgId },
    });
    if (!response.ok) return [];
    const data = await response.json() as { workspaces?: { id: string; name: string; isDefault?: boolean }[] };
    return data.workspaces ?? [];
  } catch {
    return [];
  }
}

// Export the wrapper class for type compatibility
export { SDKClientWrapper as MutagentSDK };
